import { type WorkOrderApprovalFlowSummary } from "./APIApprovalFlow";
import { type APICompanyResponse } from "./APICompanyResponse";
import { type APICustomModelResponse } from "./APICustomModelResponse";
import { type APIDocumentResponse } from "./APIDocument";
import { type APIPollAnswerResponse } from "./APIPollAnswerResponse";
import { type APIPollResponse } from "./APIPollResponse";
import { type APISiteVisitResponse } from "./APISiteVisitResponse";
import { type APITagResponse } from "./APITagResponse";
import { type APITeamShort, type APITeamValidOption } from "./APITeams";
import { type APITemplateResponse } from "./APITemplate";
import { type APIUserResponse } from "./APIUserResponse";
import { type APIWorkOrderOnHoldSummary } from "./APIWorkOrderOnHold";
import {
  type APIWorkOrderTypeResponse,
  type APIWOSection
} from "./APIWorkOrderTypeResponse";
import { type APIWorkspaceResponse } from "./APIWorkspaceResponse";

type WOPhoto = {
  name: string;
  photo: string;
  pk: number;
  url: string;
};

export enum WorkOrderStatus {
  ToDo = "to do",
  Scheduled = "scheduled",
  PendingApproval = "pending_approval",
  Completed = "completed",
  InProgress = "in_progress",
  OnHold = "on_hold",
  NeedFix = "needs_fix",
  InvalidWork = "invalid_work",
  Cancelled = "cancelled",
  Unscheduled = "unscheduled",
  Unassigned = "unassigned"
}
export enum WorkOrderConditions {
  incomplete_evidence = "incomplete_evidence",
  auto_checkout = "auto_checkout",
  overdue = "overdue",
  unassigned = "unassigned",
  unscheduled = "unscheduled",
  needs_fix = "needs_fix",
  returned_from_rejection = "returned_from_rejection"
}

export enum WorkOrderPriority {
  None = "no priority",
  Low = "low",
  Medium = "medium",
  High = "high"
}

export enum WorkOrderType {
  Reactive = "r", // reactive
  Preventive = "p", // preventive
  Other = "o" // other
}

export type WorkOrderApprovalStatus =
  | "approval_not_required"
  | "not_submitted"
  | "pending_approval"
  | "in_review"
  | "approved"
  | "corrections_required"
  | "returned_to_scheduling";
export type WorkOrderGenerationSource =
  | "manual"
  | "field_initiated"
  | "maintenance"
  | "bulk"
  | "planned"
  | "unplanned";

export type WorkOrderCheckOutType = "auto" | "manual" | null;

export enum APIWOStatus {
  required = "required",
  partially_completed = "partially completed",
  completed = "completed",
  not_completed = "not_completed"
}
export type APIWODocumentation = {
  type: APIWOSection["name"];
  nombre: string;
  required?: boolean | null;
  reviewed?: string | null;
  completed: APIWOStatus | boolean | string;
  missing?: boolean;
  tool_id?: number | string;
  form_id?: number | string;
  template_id?: number | string;
  answer_id?: number | string;
};
export type EvidenceResponse<T> = Partial<T & { required?: boolean }>;
export type WOEvidenceRequest = {
  url: string;
  required: boolean;
};
export type CountingDoc = {
  type: "form" | "checklist" | "media_template";
  count: number;
  completed: number;
};

export type APIUnplannedReport = {
  id: number;
  url: string;
  result?: string | { name: string; id?: number | string; url?: string };
  work_reason: string | { name: string; id?: number | string; url?: string };
  work_performed: string | { name: string; id?: number | string; url?: string };
  description: string;
  material_used: boolean | null;
  material_description: string;
  pending_actions: boolean | null;
  pending_description: string;
  risk_detected: boolean | null;
  risk_description: string;
  technician: Partial<APIUserResponse>;
  site_visit: string | { url: string };
  evidence: Array<string | { url: string; file?: string; id: number | string }>;
  pending_issue?: string;
};
export type APIUnplannedReportRequest = {
  result?: string;
  work_reason: string;
  work_performed: string;
  description: string;
  material_used: boolean | null;
  material_description: string;
  pending_actions: boolean | null;
  pending_description: string;
  risk_detected: boolean | null;
  risk_description: string;
  technician: string;
  site_visit: string;
  evidence_media: Array<string>;
  pending_issue?: string;
};

export type APIPendingAssetUpdateSource = {
  poll_answer_id?: number | string;
  question_answer_id?: number | string;
  poll_id?: number | string;
  form_name?: string;
  submitted_by?: { id: number | string; name: string };
  submitted_at?: string;
};

export type APIPendingAssetUpdateItem = {
  id: number | string;
  status: "pending" | "applied" | "discarded" | "superseded";
  asset: {
    id: number | string;
    title: string;
    url: string;
  };
  field: {
    id: number | string;
    key: string;
    label: string;
  };
  current_value: string | number;
  proposed_value: string | number;
  source: APIPendingAssetUpdateSource;
};

export type APIPendingAssetUpdateByPollAnswer = {
  poll_answer_id: number | string;
  count: number;
};

export type APIPendingAssetUpdatesResponse = {
  work_order: {
    id: number;
    serial: string;
    display_title: string;
  };
  count: number;
  items: APIPendingAssetUpdateItem[];
  by_poll_answer: APIPendingAssetUpdateByPollAnswer[];
};
export type EvidenceRequirementResponse = {
  id: string | number;
  evidence_type: "checklist" | "form" | "media_checklist";
  evidence_id: string | number;
  name: string;
  required: boolean;
  completed: boolean;
  missing: boolean;
  answer_ids: (string | number)[];
  answer_id: string | number;
  review_status: string;
  fulfilled_document: string | number;
};

export type EvidenceRowResponse = {
  id: number;
  evidence_type: "checklist" | "form" | "media_checklist";
  evidence_id: number;
  name: string;
  required: boolean;
  completed: boolean;
  missing: boolean;
  reviewed?: string | null;
  answer_id?: string | number;
  fulfilled_document_id?: string | number;
  tool_id?: string | number;
};
export type EvidenceRequirementSummary = {
  total_required: number;
  completed_required: number;
  missing_required: number;
  can_submit: boolean;
};
export type APIPendingAssetUpdate = {
  id: string | number;
  asset_id: string | number;
  asset_title: string;
  field_key: string;
  field_label?: string;
  current_value: string | number;
  proposed_value: string | number;
  source: APIPendingAssetUpdateSource;
};

export type APIWorkOrderResponse = {
  pk: number;
  url: string;
  asset?: Partial<APICustomModelResponse>;
  related_asset?: Partial<APICustomModelResponse>;
  type?: APIWorkOrderTypeResponse;
  subject: string;
  description: string;
  notes: string;
  additional_information: any;
  photos?: WOPhoto[];
  date?: Date | string;
  due_date?: Date | string;
  due_time?: Date | string;
  estimated_hours?: number;
  estimated_minutes?: number;
  datefixed?: Date | string;
  position?: string;
  assigned_to?: Partial<APICompanyResponse>;
  assigned_to_user?: Partial<APIUserResponse>;
  assigned_users?: Partial<APIUserResponse>[];
  audit_after_visit?: number;
  status: WorkOrderStatus;
  status_label?: string;
  status_legacy?: string;
  priority?: WorkOrderPriority;
  procedure?: APIDocumentResponse[];
  checklists?: EvidenceResponse<APIPollResponse>[];
  templates?: EvidenceResponse<APITemplateResponse>[];
  workspace?: APIWorkspaceResponse;
  work_type?: WorkOrderType;
  total_time?: string | number;
  working_time?: string | number;
  created_by?: Partial<APIUserResponse>;
  audit?: EvidenceResponse<APIPollResponse>[];
  audit_answer?: Partial<APIPollAnswerResponse>[];
  modified: string | Date;
  serial?: string;
  conditions?: WorkOrderConditions[];
  conditions_legacy?: string[];
  tags?: (APITagResponse | string)[];
  required_documentation?: APIWODocumentation[];
  site_visits?: Partial<APISiteVisitResponse>[];
  reviewers?: Partial<APIUserResponse>[];
  summary_connections?: CountingDoc[];
  approval_status?: WorkOrderApprovalStatus | string;
  requires_approval?: boolean;
  required_approval_level?: number | null;
  current_reviewer?: Partial<APIUserResponse> | null;
  reviewer_assigned_at?: string | null;
  review_started_at?: string | null;
  approved_by?: Partial<APIUserResponse> | null;
  approved_at?: string | null;
  can_start_review?: boolean;
  can_release_review?: boolean;
  can_transfer_review?: boolean;
  can_assign_reviewer?: boolean;
  can_approve?: boolean;
  can_reject?: boolean;
  can_undo_approval?: boolean;
  /** Report + Inventory approval flow (#3441); `null` = legacy approval. */
  approval_flow?: WorkOrderApprovalFlowSummary | null;
  unplanned_report?: Partial<APIUnplannedReport>;
  is_archived?: boolean;
  archived_at?: string | Date;
  archived_by?: Partial<APIUserResponse>;
  archive_reason?: string;
  external_data?: Partial<
    {
      ticket_number: string;
    } & Record<string, string | number | boolean>
  >;
  execution_owner?: Partial<APIUserResponse>;
  /** Pause in force (`null` when not On Hold), #3458. */
  on_hold?: APIWorkOrderOnHoldSummary | null;
  display_title: string;
  unplanned_activity_type?: {
    id: number;
    name: string;
    code: string;
  } | null;
  job_type_version?: number | null;
  job_type_snapshot_source?: string | null;
  enablement_inventory?: boolean;
  generation_source?: WorkOrderGenerationSource;
  source_display?: string;
  bulk_operation_id?: string | number | null;
  bulk_operation_name?: string | null;
  check_out_type?: WorkOrderCheckOutType;
  evidence_rows?: EvidenceRowResponse[];
  required_evidence_summary?: EvidenceRequirementSummary;
  evidence_requirements?: EvidenceRequirementResponse[];
  responsible_team_id?: number | null;
  responsible_team?: APITeamShort | null;
  assignment_mode?: "users" | "team";
  assignment_method?: "direct" | "team_pool" | "unassigned";
  assigned_to_team?: Partial<APITeamShort>;
  assigned_to_team_id?: number | string;
};
export type APIWorkOrderRequest = {
  asset?: string;
  type?: string | number;
  description: string;
  subject: string;
  notes: string;
  additional_information: any;
  photos?: File[];
  date?: Date | string;
  datefixed?: Date | string;
  due_date?: Date | string;
  due_time?: Date | string;
  estimated_hours?: number;
  estimated_minutes?: number;
  position?: string;
  assigned_to?: string;
  assigned_to_user?: string;
  assigned_users?: string[];
  audit?: WOEvidenceRequest[];
  checklists?: WOEvidenceRequest[];
  templates?: WOEvidenceRequest[];
  audit_after_visit?: number;
  status: WorkOrderStatus;
  priority?: WorkOrderPriority;
  procedure?: string[];
  workspace: string;
  work_type?: WorkOrderType;
  assets?: string[];
  tags?: string[];
  related_asset?: string;
  is_archived?: boolean;
  archived_at?: string | Date;
  archived_by?: Partial<APIUserResponse>;
  archive_reason?: string;
  unplanned_report?: Partial<APIUnplannedReport>;
  external_data?: Partial<
    {
      ticket_number: string;
    } & Record<string, string | number | boolean>
  >;
  documents?: WOEvidenceRequest[];
  assignment_method?: "direct" | "team_pool" | "unassigned";
  assigned_to_team_id?: number | string;
  responsible_team_id?: string | number;
  assignment_mode?: "users" | "team";
  assigned_to_team?: Partial<APITeamShort>;
};
/**
 * Body accepted by the create/update endpoints of a Work Order.
 *
 * `status` is intentionally absent: a Work Order status can only change through
 * its dedicated action endpoints (start-work, resume, on-hold, complete-work,
 * submit-for-approval, approve, reject, undo-approve, admin-cancel), never
 * through the ordinary write endpoints.
 */
export type WorkOrderWriteRequest = Omit<APIWorkOrderRequest, "status">;
export type AdminCancelWorkOrderRequest = {
  reason: string;
};
export type APIWOHistoryRecord = {
  pk: number | string;
  status: APIWorkOrderResponse["status"] | "message";
  message: string;
  created_by: Partial<APIUserResponse>;
  created: string | Date;
  title?: string;
  event_key?: string;
  metadata?: Record<string, any>;
  created_at?: string;
  legacy?: boolean;
  photo?: string;
  photo_thumbnail?: string;
  photo_thumbnail2?: string;
  actor?: Partial<APIUserResponse & { type?: "user" | "system" | string }>;
};

/**
 * Codes of the `responsible_team_id` error of PUT/PATCH /api/workorders/{id}/:
 *  - responsible_team_required  → multiple valid teams, frontend must choose
 *  - no_valid_responsible_team  → assigned user has no valid team
 *  - invalid_responsible_team   → sent team is not valid
 *  - responsible_team_without_assignees → team sent without assigned users
 */
export type APIResponsibleTeamErrorCode =
  | "responsible_team_required"
  | "no_valid_responsible_team"
  | "invalid_responsible_team"
  | "responsible_team_without_assignees";

/**
 * Error body returned by backend under `responsible_team_id` (HTTP 400).
 * DRF renders the leaves as strings, so `valid_teams[].id` arrives as "378".
 */
export type APIResponsibleTeamError = {
  detail: string;
  code: APIResponsibleTeamErrorCode;
  valid_teams: APITeamValidOption[];
};

/** Shape of each user in the eligible-reviewers endpoint response */
export type APIEligibleReviewer = {
  id: number;
  url: string;
  name: string;
  first_name: string;
  last_name: string;
  email: string;
  photo: string | null;
};
